import React from "react";
import { FullScreenDialog } from "../FullScreenDialog";
import { Paper } from "@material-ui/core";
import { ServiceApprovalForm, ServiceApprovalFormValues } from "./ServiceApprovalForm";
import { ApprovalInfo, EjiService, Job, Query, QueryGetJobArgs, VehicleInfo } from "../../generated/nest-graphql";
import { useAxiosClient } from "../../lib/nestBackendClient";
import { useDispatch } from "../../contexts/snackbar-context";
import { showErrorAlert, showSuccessAlert } from "../../actions";
import { useQuery } from "@apollo/client";
import { GET_JOB } from "../../graphql/queries/getJob";

export type ServiceApprovalDialogProps = {
  open: boolean;
  onClose: () => void;
  jobId: string;
  approvalInfo?: ApprovalInfo | null;
};

export const ServiceApprovalDialog: React.FC<ServiceApprovalDialogProps> = ({ open, onClose, jobId, approvalInfo }) => {
  const dispatch = useDispatch();
  const nestBackendClient = useAxiosClient();
  const [diagnosticServices, setDiagnosticServices] = React.useState<EjiService[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const { data: jobData, refetch: refetchJob } = useQuery<Query, QueryGetJobArgs>(GET_JOB, {
    variables: { id: jobId },
    fetchPolicy: "cache-first",
    returnPartialData: true,
  });
  const job = jobData?.getJob as Job | undefined;

  const witDiagnostic = job?.diagnosticJob?.id || job.preJobCheckListV2 || job.preJobCheckList;

  React.useEffect(() => {
    if (!open) return;

    const fetchDiagnosticServices = async () => {
      if (!witDiagnostic) {
        setDiagnosticServices(null);
        return;
      }
      setLoading(true);
      try {
        const response = await nestBackendClient.get<EjiService[]>(`jobs/diagnostic-services/${jobId}`);
        setDiagnosticServices(response.data);
      } catch (e) {
        setDiagnosticServices(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnosticServices();
  }, [open, jobId, nestBackendClient]);

  const effectiveServices = witDiagnostic ? diagnosticServices || [] : job?.services || [];

  const mapApprovalStatusToSelection = (status?: string | null): boolean | null => {
    if (status === "Approved") return true;
    if (status === "Not Approved") return false;
    return null;
  };

  const defaultInitialValues: ServiceApprovalFormValues = {
    customerContacted: approvalInfo?.spokeTo || "",
    serviceSelections: effectiveServices.map((service) => mapApprovalStatusToSelection(service.approvalStatus)),
  };

  const handleSubmitApprovals = async (values: ServiceApprovalFormValues) => {
    if (!effectiveServices.length) {
      onClose();
      return;
    }

    const servicesPayload = effectiveServices.map((service, index) => {
      const selection = values.serviceSelections[index];
      let approvalStatus: string | undefined;

      if (selection === true) {
        approvalStatus = "Approved";
      } else if (selection === false) {
        approvalStatus = "Not Approved";
      } else {
        approvalStatus = service.approvalStatus || "Pending";
      }

      return {
        service: service.service,
        approvalStatus,
      };
    });

    const payload = {
      jobId,
      services: servicesPayload,
      approvalInfo: {
        submittedBy: approvalInfo?.submittedBy,
        approvalMethod: "direct",
        spokeTo: values.customerContacted || approvalInfo?.spokeTo,
      },
    };

    try {
      await nestBackendClient.put("jobs/update-approval", payload);
      await refetchJob();
      showSuccessAlert(dispatch, "Approvals updated");
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Unable to update approvals";
      showErrorAlert(dispatch, message);
    }
  };

  return (
    <FullScreenDialog title="Service Approval" open={open} onClose={onClose} headerPosition="fixed">
      <Paper className="pt-20">
        {!loading && effectiveServices.length > 0 && (
          <ServiceApprovalForm
            services={effectiveServices}
            onSubmitApprovals={handleSubmitApprovals}
            initialValues={defaultInitialValues}
            vehicleInfo={
              {
                ...job.vehicleInfo,
                ...job.vehicle,
              } as VehicleInfo
            }
          />
        )}
      </Paper>
    </FullScreenDialog>
  );
};
